import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Row, Col, Form, Spinner, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { IconArrowsSplit } from '@tabler/icons-react';
import { parseISO, isAfter, isBefore, addDays, subDays, format, differenceInDays } from 'date-fns';
import useShowBooking from '../../../../services/booking/hooks/useShowBooking';
import useSplitReservation from '../../../../services/booking/hooks/v4/useSplitReservation';
import FormSpinner from '../../../../shared/components/spinner/FormSpinner';
import DateInput from '../../../../shared/components/form/DateInput';

interface SplitReservationModalProps {
    show: boolean;
    onClose: () => void;
    bookingId: string;
}

const SplitReservationModal: React.FC<SplitReservationModalProps> = ({ show, onClose, bookingId }) => {
    const [splitDate, setSplitDate] = useState('');
    const [errorMsg, setErrorMsg] = useState('');

    // Hooks
    const { data: booking, isLoading: isLoadingBooking } = useShowBooking({ bookingId });
    const { mutate: splitReservation, isPending: isSubmitting } = useSplitReservation();

    // Date Constraints Logic
    const { minDate, maxDate, isSplitable } = useMemo(() => {
        if (!booking) return { minDate: '', maxDate: '', isSplitable: true };

        const ci = parseISO(booking.data.checkin_date);
        const co = parseISO(booking.data.checkout_date);
        const diff = differenceInDays(co, ci);

        return {
            minDate: format(addDays(ci, 1), 'yyyy-MM-dd'),
            maxDate: format(subDays(co, 1), 'yyyy-MM-dd'),
            isSplitable: diff > 1
        };
    }, [booking]);

    useEffect(() => {
        if (show) {
            setSplitDate('');
            setErrorMsg('');
        }
    }, [show]);

    // Set default split date to minDate once booking is loaded
    useEffect(() => {
        if (show && isSplitable && minDate && !splitDate) {
            setSplitDate(minDate);
        }
    }, [show, isSplitable, minDate, splitDate]);

    const handleSplit = () => {
        if (!booking || !isSplitable) return;
        
        setErrorMsg('');
        
        const checkin = parseISO(booking.data.checkin_date);
        const checkout = parseISO(booking.data.checkout_date);
        const selectedDate = parseISO(splitDate);

        if (!splitDate) {
            setErrorMsg('Please select a split date');
            return;
        }

        // Final Submission Guard (Exclusive check)
        if (!isAfter(selectedDate, checkin) || !isBefore(selectedDate, checkout)) {
            setErrorMsg(`Split date must be strictly between ${booking.data.checkin_date} and ${booking.data.checkout_date}`);
            return;
        }

        const payload = {
            booking_room_id: bookingId,
            split_date: splitDate
        };

        console.log("Split Payload:", payload);

        splitReservation(payload, {
            onSuccess: () => {
                onClose();
            }
        });
    };

    return (
        <Modal show={show} onHide={onClose} centered>
            <Modal.Header closeButton className="bg-light">
                <Modal.Title className="fs-5">
                    <IconArrowsSplit className="me-2 text-primary" />
                    Split Booking
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                {isLoadingBooking ? (
                    <FormSpinner />
                ) : (
                    <>
                        <div className="mb-4">
                            <p className="text-muted small mb-3">
                                Splitting a booking will create two separate reservations at the selected date.
                            </p>
                            <Row className="text-center g-0 border rounded bg-white overflow-hidden">
                                <Col xs={5} className="p-2 border-end">
                                    <small className="text-muted d-block">Check-in</small>
                                    <div className="fw-bold">{booking?.data.checkin_date}</div>
                                </Col>
                                <Col xs={2} className="d-flex align-items-center justify-content-center bg-light">
                                    <IconArrowsSplit size={20} className="text-muted" />
                                </Col>
                                <Col xs={5} className="p-2">
                                    <small className="text-muted d-block">Check-out</small>
                                    <div className="fw-bold">{booking?.data.checkout_date}</div>
                                </Col>
                            </Row>
                        </div>

                        {!isSplitable ? (
                            <Alert variant="warning" className="small">
                                <p className="mb-0 fw-semibold">This reservation cannot be split because it is only for 1 night.</p>
                            </Alert>
                        ) : (
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">Select Split Date</Form.Label>
                                <div className="position-relative">
                                    <DateInput
                                        className="form-control"
                                        value={splitDate}
                                        onChange={(e) => setSplitDate(e.target.value)}
                                        min={minDate}
                                        max={maxDate}
                                        disabled={!isSplitable}
                                    />
                                </div>
                                <Form.Text className="text-muted">
                                    The booking will be cut on this date. Valid dates: {minDate} to {maxDate}.
                                </Form.Text>
                            </Form.Group>
                        )}

                        {errorMsg && (
                            <Alert variant="danger" className="py-2 small mt-3">
                                {errorMsg}
                            </Alert>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer className="bg-light">
                <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
                {(() => {
                    const isButtonDisabled = isSubmitting || !splitDate || !isSplitable || isLoadingBooking;
                    const showTooltip = !splitDate || !isSplitable;

                    const ConfirmButton = (
                        <Button
                            variant="primary"
                            onClick={handleSplit}
                            disabled={isButtonDisabled}
                            style={isButtonDisabled ? { pointerEvents: 'none' } : {}}
                        >
                            {isSubmitting ? <Spinner animation="border" size="sm" /> : 'Confirm Split'}
                        </Button>
                    );

                    if (showTooltip) {
                        return (
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip id="tooltip-split-invalid">
                                        {!splitDate
                                            ? 'Please select a split date.'
                                            : 'The split date must be strictly between the check-in and check-out dates.'}
                                    </Tooltip>
                                }
                            >
                                <span className="d-inline-block" style={{ cursor: 'not-allowed' }}>
                                    {ConfirmButton}
                                </span>
                            </OverlayTrigger>
                        );
                    }

                    return ConfirmButton;
                })()}
            </Modal.Footer>
        </Modal>
    );
};

export default SplitReservationModal;
